import React, { ReactNode, useEffect, useMemo, useRef } from 'react'

import { MapPinIcon } from '@heroicons/react/24/outline'
import cn from 'classnames'
import { ErrorMessage, useField } from 'formik'
import _debounce from 'lodash/debounce'

import { InputField } from '@/components/FormikInputs'
import Spinner from '@/components/Spinner'
import { useLazyGetProfileQuery, useLazyGetProfilesQuery } from '@/services/profile'
import { IUser } from '@/types/services'

const NoOp: VoidFunction = () => {
  return
}

interface IHint {
  user: IUser
  onClick: () => void
}

function Hint({ user, onClick }: IHint) {
  return (
    <li
      onClick={onClick}
      className='odd:bg-unlock-icegray p-3 even:bg-white cursor-pointer'
    >
      <div className='text-sm text-unlock-gray font-semibold'>{user.email}</div>
      <span className='capitalize text-unlock-gray'>
        {user.first_name} {user.last_name}
      </span>
    </li>
  )
}

interface IEmailInput {
  name: string
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  emailHintsClassnames?: string
  isRequired?: boolean
  showIcon?: boolean
  button?: ReactNode
  autoFocus?: boolean
  handleSearchSuccessful?: (user: IUser) => void
  customClasses?: string
  style?: 'minimal' | 'default' | 'none'
}

export function EmailInput({
  name,
  placeholder = '',
  disabled = false,
  readOnly = false,
  emailHintsClassnames = '',
  showIcon = false,
  autoFocus = false,
  button,
  handleSearchSuccessful,
  customClasses = null,
  style = 'default',
}: IEmailInput) {
  const [field, , helpers] = useField({ name })
  const [isShowingEmailHints, setIsShowingEmailHints] = React.useState(false)
  const inputRef = React.useRef(null)
  const [getEmailList, { data: emailListData, isLoading: isEmailListLoading }] =
    useLazyGetProfilesQuery()
  const [getProfileDetails, { isLoading: isProfileDetailsLoading }] = useLazyGetProfileQuery()
  const debounceFn = useMemo(() => _debounce((e) => handleGetEmailList(e), 500), [])
  const dropdownRef = useRef(null)

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsShowingEmailHints(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const onInputChange = async (event) => {
    // Convert email to lowercase
    const { value } = event.target
    const lowerCaseValue = value.toLowerCase()
    helpers.setValue(lowerCaseValue)
    debounceFn(lowerCaseValue)
  }

  const handleGetEmailList = async (value) => {
    if (value.length === 0) {
      setIsShowingEmailHints(false)
      return
    }
    setIsShowingEmailHints(true)

    const params = { email__icontains: value }
    await getEmailList({ ...params }).unwrap()
  }

  const onEmailSelected = async (email: IUser) => {
    let selectedUser = null
    try {
      selectedUser = await getProfileDetails(email.id).unwrap()
    } catch (error) {
      console.error(error)
    }

    if (!selectedUser) return
    helpers.setValue(selectedUser.email)
    helpers.setTouched(true)
    setIsShowingEmailHints(false)
    if (handleSearchSuccessful) handleSearchSuccessful(selectedUser)
  }

  const inputWrapperClasses = cn('field-text', {
    'read-only-field pr-0': readOnly,
    'disabled-field': disabled,
  })

  const isLoading = isEmailListLoading || isProfileDetailsLoading

  return (
    <div ref={dropdownRef}>
      <div className={`w-full`}>
        <div className={`${inputWrapperClasses}`}>
          {showIcon && (
            <MapPinIcon
              className='text-unlock-gray lg:mx-4'
              width={26}
            />
          )}
          <InputField
            value={field.value}
            ref={inputRef}
            name={name}
            type='text'
            style={style}
            data-test='manuallycreatemaxdeal-email-input'
            placeholder={placeholder}
            onChange={onInputChange}
            onBlur={() => !isShowingEmailHints && helpers.setTouched(true)}
            onClick={() => {
              if (field.value) setIsShowingEmailHints(true)
            }}
            disabled={disabled}
            readOnly={readOnly}
            autoComplete='new-password'
            autoFocus={autoFocus}
            customClasses={customClasses}
          />
        </div>
        {button}
      </div>

      {isShowingEmailHints && (
        <div className='absolute z-10'>
          {emailListData && emailListData?.length > 0 && (
            <div
              className={`w-[130%] bg-white border border-unlock-icegray shadow-xl ${emailHintsClassnames}`}
              style={{ zIndex: 999 }}
            >
              {emailListData && emailListData?.length > 0 ? (
                <ul
                  className='h-[250px] overflow-y-auto'
                  data-test='manuallycreatemaxdeal-email-list'
                >
                  {emailListData.map((email, i) => (
                    <>
                      <Hint
                        key={i}
                        user={email}
                        onClick={() => onEmailSelected(email)}
                      />
                      <hr
                        className='h-[1px] border-none w-full border-gray-200'
                        style={{ borderTop: '2px dotted' }}
                      />
                    </>
                  ))}
                </ul>
              ) : null}
            </div>
          )}
          {isLoading && (
            <div className='w-[130%] bg-white border border-unlock-icegray shadow-xl mx-auto'>
              <div className="w-full min-h-[50px] justify-center items-center data-test='spinloading-animation'">
                <Spinner className='mt-4' />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface IRichEmailInput extends IEmailInput {
  label: string
  className?: string
}

export function RichEmailInput({ label, name, className = '', button, ...props }: IRichEmailInput) {
  return (
    <div
      key={name}
      className={`${className}`}
    >
      <div className='relative'>
        <div className='mb-2'>
          <label
            className='mb-2 text-sm !text-black text-weight-600'
            htmlFor={name}
          >
            {label}
          </label>
        </div>
        <EmailInput
          name={name}
          button={button}
          {...props}
        />
      </div>
      <ErrorMessage name={name}>
        {(msg) => <p className='label-text-alt text-red-500'>{msg}</p>}
      </ErrorMessage>
    </div>
  )
}
